import { useRef, useState, useEffect, useMemo } from "react";

import { useApp } from "src/components/App/Context";
import useAudioController from "./useAudioController";
import time from "src/utils/time";

export const Status = {
  PLAYING: "playing",
  RECORDING: "recording",
  PAUSED_HAS_RECORDING: "pausedHasRecording",
  PAUSED_NO_RECORDING: "pausedNoRecording",
  LOADING: "loading"
};

export default function useAudioRecorder({
  getInitialRecordingSrc,
  maxAllowedDuration
} = {}) {
  const app = useApp();
  const mediaRecorderRef = useRef();
  const durationWatcherIntervalRef = useRef();
  const recordingStartDateRef = useRef();
  const recordingEndDateRef = useRef();

  const [requestingPermissions, setRequestingPermissions] = useState(false);
  const [audioData, setAudioData] = useState();
  const [status, setStatus] = useState(
    getInitialRecordingSrc
      ? Status.PAUSED_HAS_RECORDING
      : Status.PAUSED_NO_RECORDING
  );

  const audioDataSrc = useMemo(() => {
    if (audioData == null) return null;
    const blob = new Blob([audioData], { type: "audio/ogg" });
    return URL.createObjectURL(blob);
  }, [audioData]);

  const audioController = useAudioController({
    srcResolver: getInitialRecordingSrc,
    onEnded: () => setStatus(Status.PAUSED_HAS_RECORDING)
  });

  useEffect(() => () => clearInterval(durationWatcherIntervalRef.current), []);

  async function play() {
    if (status === Status.RECORDING) return;
    setStatus(Status.LOADING);
    const error = await audioController.play({ src: audioDataSrc });
    error ? setStatus(Status.PAUSED_HAS_RECORDING) : setStatus(Status.PLAYING);
  }

  async function initializeMediaRecorder() {
    setRequestingPermissions(true);

    const status = await app.permissions.getMediaAccessStatus("microphone");
    let hasPermission = status === "granted";

    if (status === "not-determined") {
      hasPermission = await app.permissions.resolveMediaPermission(
        "microphone"
      );
    }
    setRequestingPermissions(false);

    if (!hasPermission) {
      status === "denied" && app.permissions.openDialog("microphone");
      return;
    }
    mediaRecorderRef.current = new window.Recorder({
      encoderPath: "./opus-recorder/dist/encoderWorker.min.js",
      maxFramesPerPage: 5
    });
    mediaRecorderRef.current.ondataavailable = (arrayBuffer) => {
      setAudioData(arrayBuffer);
      setStatus(Status.PAUSED_HAS_RECORDING);
    };
  }

  async function record() {
    if (requestingPermissions || status === Status.RECORDING) return;
    recordingEndDateRef.current = null;

    if (mediaRecorderRef.current == null) {
      await initializeMediaRecorder();
      if (mediaRecorderRef.current == null) return;
    }
    setStatus(Status.RECORDING);

    mediaRecorderRef.current.start();
    recordingStartDateRef.current = Date.now();
    if (maxAllowedDuration) {
      durationWatcherIntervalRef.current = setInterval(
        maxAllowedDurationWatcher,
        100
      );
    }
  }
  function maxAllowedDurationWatcher() {
    if (getRecordingTime() >= maxAllowedDuration) {
      stopRecording();
    }
  }

  function stopRecording() {
    if (requestingPermissions) return;
    clearInterval(durationWatcherIntervalRef.current);
    mediaRecorderRef.current.stop();
    recordingEndDateRef.current = Date.now();
  }
  function stopPlaying() {
    setStatus(Status.PAUSED_HAS_RECORDING);
    audioController.pause();
  }
  function getCurrentTime() {
    return audioController.getCurrentTime();
  }
  function getRecordingTime() {
    if (!recordingStartDateRef.current) {
      return 0;
    }
    if (recordingEndDateRef.current) {
      return time(recordingEndDateRef.current).diff(
        recordingStartDateRef.current
      );
    }
    return time().diff(recordingStartDateRef.current);
  }
  function getRecordingDuration() {
    return new Promise((resolve) => {
      const audio = new Audio(audioDataSrc);
      audio.addEventListener("loadedmetadata", () =>
        resolve(audio.duration * 1000)
      );
    });
  }
  return {
    audioData,
    Status,
    play,
    record,
    stopRecording,
    stopPlaying,
    getCurrentTime,
    getRecordingDuration,
    getRecordingTime,
    status,
    hasPlayError: audioController.hasPlayError,
    clearPlayError: audioController.clearPlayError
  };
}
